/**
 * =============================================================================
 * IDS CRM Web - Is this ticket or job on the Ops Planning board?
 * =============================================================================
 *
 * Gareth, 21 October 2026 (docs/OPS_BOARD_SERVICE_CARDS_PLAN.md): a ticket,
 * engineer work or a maintenance visit sometimes needs the planning meeting.
 * Shown on the record's own page (and the Jobs & Tickets board panel):
 *  - on the board: "On the Ops Planning board - <column>, since <date>
 *    (<reason>)" with a link to the board, and for team leaders and up a
 *    "Take off the board" button;
 *  - not on the board: for team leaders, managers and admins, an "Add to Ops
 *    Planning" button opening a small dialog: why (Parts required / R&D fix
 *    required / Other, which needs a note), an optional note, and the column
 *    (Holding unless another is chosen).
 * Everyone else sees nothing unless the work is on the board.
 * =============================================================================
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/format';
import './OpsBoardServiceStatus.css';

export const REASON_NOTE_MAX = 500;
const REASON_ORDER = ['parts_required', 'rd_fix_required', 'other'];

async function call(authFetch, path, options = {}) {
  const response = await authFetch(`/api/v1/ops-board${path}`, options);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || 'That could not be saved. Try again.');
  return data;
}

function AddDialog({ type, id, info, onCancel, onAdded }) {
  const { authFetch } = useAuth();
  const holding = (info.columns || []).find(column => column.is_holding);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [columnId, setColumnId] = useState(holding ? holding.column_id : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const needsNote = reason === 'other';
  const ready = reason && (!needsNote || note.trim());

  const add = async () => {
    if (!ready) return;
    setSaving(true);
    setError('');
    try {
      await call(authFetch, '/service-cards', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ record_type: type, record_id: id, reason, reason_note: note.trim() || null, column_id: columnId || null })
      });
      onAdded();
    } catch (err) {
      setError(err.message === 'Failed to fetch' ? 'That could not be sent. Check your signal and try again.' : err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" role="presentation">
      <div className="modal-content ops-service-dialog" role="dialog" aria-modal="true" aria-labelledby="ops-service-title">
        <div className="modal-header">
          <h2 id="ops-service-title">Add to Ops Planning</h2>
          <button type="button" className="modal-close" onClick={onCancel} disabled={saving} aria-label="Close">&times;</button>
        </div>
        <div className="modal-body">
          <p className="ops-service-intro">It goes on the planning board for the next meeting. Its status here does not change.</p>
          <fieldset className="ops-service-reasons">
            <legend>Why does it need the planning meeting?</legend>
            {REASON_ORDER.filter(value => info.service_reasons && info.service_reasons[value]).map(value => (
              <label key={value}>
                <input type="radio" name="ops-service-reason" value={value} checked={reason === value} onChange={() => setReason(value)} disabled={saving} />
                {info.service_reasons[value]}
              </label>
            ))}
          </fieldset>
          <label className="ops-service-field" htmlFor="ops-service-note">
            {needsNote ? 'Say why (needed)' : 'Note (optional)'}
          </label>
          <textarea
            id="ops-service-note"
            value={note}
            maxLength={REASON_NOTE_MAX}
            onChange={(e) => setNote(e.target.value)}
            disabled={saving}
            rows={3}
          />
          <span className="ops-service-count">{note.length} / {REASON_NOTE_MAX}</span>
          <label className="ops-service-field" htmlFor="ops-service-column">Column</label>
          <select id="ops-service-column" value={columnId} onChange={(e) => setColumnId(e.target.value)} disabled={saving}>
            {(info.columns || []).map(column => <option key={column.column_id} value={column.column_id}>{column.name}</option>)}
          </select>
          {error && <p className="ops-service-error">{error}</p>}
          <div className="ops-service-actions">
            <button type="button" className="btn-secondary btn-small" onClick={onCancel} disabled={saving}>Cancel</button>
            <button type="button" className="btn-primary btn-small" onClick={add} disabled={saving || !ready}>
              {saving ? 'Adding…' : 'Add to the board'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Which tickets, jobs and visits are on the Ops Planning board, for tagging
 * cards on the Jobs & Tickets views: a Map of "<type>:<id>" to its column.
 * Read again whenever `refresh` changes.
 */
export function useOnOpsBoard(refresh) {
  const { authFetch } = useAuth();
  const [onBoard, setOnBoard] = useState(() => new Map());
  useEffect(() => {
    let cancelled = false;
    call(authFetch, '/service-cards/open')
      .then(data => {
        if (!cancelled) setOnBoard(new Map((data.data || []).map(row => [`${row.record_type}:${row.record_id}`, row.column_name])));
      })
      .catch(() => { if (!cancelled) setOnBoard(new Map()); });
    return () => { cancelled = true; };
  }, [authFetch, refresh]);
  return onBoard;
}

function OpsBoardServiceStatus({ type, id, compact = false }) {
  const { authFetch } = useAuth();
  const [info, setInfo] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!type || !id) return;
    try {
      setInfo(await call(authFetch, `/service-cards/for/${type}/${id}`));
    } catch (err) {
      // Not knowing whether it is on the board must not spoil the page.
      setInfo(null);
    }
  }, [authFetch, type, id]);

  useEffect(() => { load(); }, [load]);

  const takeOff = async () => {
    if (!info?.card || !window.confirm('Take this off the planning board?')) return;
    setBusy(true);
    setError('');
    try {
      await call(authFetch, `/service-cards/${info.card.card_id}/remove`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({})
      });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!info) return null;
  const { card } = info;
  if (!card && !(info.can_add && !info.finished)) return null;

  const reason = card ? `${info.service_reasons?.[card.reason] || card.reason}${card.reason_note ? `: ${card.reason_note}` : ''}` : '';

  return (
    <div className={`ops-service-status${compact ? ' compact' : ''}`}>
      {card ? (
        <>
          <span className="ops-service-on">
            On the Ops Planning board — <strong>{card.column_name}</strong>, since {formatDate(card.added_at)}
            {reason ? <> ({reason})</> : null}
          </span>
          <Link to="/ops-board" className="ops-service-link">View board</Link>
          {info.can_add && (
            <button type="button" className="ops-service-link-btn" onClick={takeOff} disabled={busy}>
              {busy ? 'Taking off…' : 'Take off the board'}
            </button>
          )}
        </>
      ) : (
        <button type="button" className="btn-secondary btn-small ops-service-add" onClick={() => setAdding(true)}>
          Add to Ops Planning
        </button>
      )}
      {error && <span className="ops-service-error">{error}</span>}
      {adding && (
        <AddDialog
          type={type}
          id={id}
          info={info}
          onCancel={() => setAdding(false)}
          onAdded={() => { setAdding(false); load(); }}
        />
      )}
    </div>
  );
}

export default OpsBoardServiceStatus;
